/**
 * The ways to start playing: a local match on one keyboard, or a networked room
 * that opens in the lobby and moves into a match when the host starts it.
 *
 * Local and host both run a sim from a fixed-step loop. The guest runs no sim
 * either: it predicts its own blob (which depends on nothing but its own input)
 * and draws everything else from the host's snapshots, ~80 ms behind.
 */
import {
  type BlobInput,
  Bot,
  type BotLevel,
  type Constants,
  DEFAULTS,
  type Side,
  type SimState,
  VolleySim,
  cloneConstants,
  initPhysics,
  isCharged,
} from '@office/sim';
import {
  BlobPredictor,
  GuestRoom,
  INPUT_EVERY_TICKS,
  type GuestSession,
  HostRoom,
  type HostSession,
} from '@office/net';
import { CourtRenderer } from '../games/volley/render.js';
import { FixedLoop } from '../games/volley/loop.js';
import { Keyboard, mergeInputs } from '../games/volley/input.js';
import { describeMap, loadBindings } from '../games/volley/bindings.js';
import { TOUCH_BAND_PX, TouchControls, isTouchDevice } from '../games/volley/touch.js';
import { indexWithinSide, playerLabel } from '../games/volley/colors.js';
import { TuningPanel } from './tuning/panel.js';
import { LobbyView } from './lobby-ui.js';
import { DEFAULT_GAME } from './games.js';
import { makeSignaling } from './signaling.js';

export type Teardown = () => void;

export interface StatusSink {
  (text: string): void;
}

/**
 * Debug handle. Exposed so a console (or a Playwright spec) can inspect and
 * pump the current match without going through the render loop, which the
 * browser throttles in background tabs.
 */
export interface GameHandle {
  mode: 'local' | 'bot' | 'host' | 'guest';
  sim?: VolleySim;
  host?: HostSession;
  guest?: GuestSession;
  hostRoom?: HostRoom;
  guestRoom?: GuestRoom;
  /** Live prediction state, guest side. */
  predictor?: () => BlobPredictor | null;
  /** Stop the render loop, so a test can hold a deterministic frame. */
  pause?: () => void;
  /** Advance one fixed step through the real input path. Used with `pause`. */
  step?: () => void;
  /** Draw one frame from the current state. Used with `pause`. */
  renderOnce?: () => void;
}

function expose(handle: GameHandle): void {
  (window as unknown as Record<string, unknown>)['__game'] = handle;
}

interface Stage {
  renderer: CourtRenderer;
  keyboard: Keyboard;
  touch: TouchControls | null;
  /** Input for the player sitting at this device, from whichever source. */
  localInput: () => BlobInput;
  /** Second keyboard player, for a local match. */
  secondInput: () => BlobInput;
  /** HUD text for the local keys, e.g. "A D / W / Space". */
  keysHint: string;
  secondKeysHint: string;
  dispose: Teardown;
}

function makeStage(canvas: HTMLCanvasElement, constants: Constants): Stage {
  const renderer = new CourtRenderer(canvas, constants);
  const keyboard = new Keyboard();
  const detach = keyboard.attach();
  // Read once per match: the controls panel is only reachable from the
  // launcher, so a match never sees bindings change under it.
  const keys = loadBindings();

  // Only built on a touch device: on a desktop these would be dead pixels over
  // the court, and a laptop with a touchscreen still wants the keyboard.
  const touch = isTouchDevice() ? new TouchControls() : null;
  // Hidden until a match is actually running: thumb buttons over the lobby
  // card would just be in the way.
  touch?.setVisible(false);

  const resize = (): void => renderer.resize();
  window.addEventListener('resize', resize);
  // The element can change size without the window doing so (browser chrome
  // collapsing on a phone); the observer catches that where it exists.
  const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null;
  observer?.observe(canvas);
  // Landscape/portrait flips change the canvas size without a resize on some
  // mobile browsers.
  const orientation = window.matchMedia('(orientation: portrait)');
  orientation.addEventListener('change', resize);
  renderer.resize();

  return {
    renderer,
    keyboard,
    touch,
    localInput: () =>
      touch ? mergeInputs(keyboard.read(keys.p1), touch.read()) : keyboard.read(keys.p1),
    secondInput: () => keyboard.read(keys.p2),
    keysHint: describeMap(keys.p1),
    secondKeysHint: describeMap(keys.p2),
    dispose: () => {
      detach();
      touch?.destroy();
      observer?.disconnect();
      window.removeEventListener('resize', resize);
      orientation.removeEventListener('change', resize);
    },
  };
}

function inviteUrl(room: string): string {
  const url = new URL(location.href);
  url.searchParams.set('mode', 'join');
  url.searchParams.set('room', room);
  return url.toString();
}

/** Both players on one keyboard. */
export async function startLocal(canvas: HTMLCanvasElement, status: StatusSink): Promise<Teardown> {
  await initPhysics();
  const sim = new VolleySim({ sides: [0, 1], net: true });
  const stage = makeStage(canvas, sim.constants);
  const panel = new TuningPanel(sim.constants, {
    actions: [{ label: 'Rematch', run: () => sim.resetMatch(0) }],
  });

  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'KeyR' && !panel.visible) sim.resetMatch(0);
  };
  window.addEventListener('keydown', onKey);
  status(
    stage.touch
      ? 'Solo knockabout — you are Teal'
      : 'Local match — P1 WASD, P2 arrows',
  );

  let prev = sim.snapshot();
  let next = prev;
  let inputs: BlobInput[] = [];
  // Keyboard hints are noise on a phone, and dropping them gives the court the
  // bottom band back.
  const hud = stage.touch
    ? { bottomInset: TOUCH_BAND_PX }
    : { hud: `P1  ${stage.keysHint}      P2  ${stage.secondKeysHint}      R rematch      ~ tuning` };
  const draw = (alpha: number): void => {
    stage.touch?.setEnergy(next.blobs[0]?.energy ?? 1);
    stage.renderer.draw(prev, next, alpha, {
      net: true,
      match: next.match,
      charged: chargedFlags(next, inputs),
      ...hud,
    });
  };

  const stepOnce = (): void => {
    prev = next;
    inputs = [stage.localInput(), stage.secondInput()];
    sim.step(inputs);
    next = sim.snapshot();
  };

  const loop = new FixedLoop({ step: stepOnce, render: draw });
  stage.touch?.setVisible(true);
  loop.start();
  expose({
    mode: 'local',
    sim,
    pause: () => loop.stop(),
    step: stepOnce,
    renderOnce: () => {
      prev = sim.snapshot();
      next = prev;
      draw(0);
    },
  });

  return () => {
    loop.stop();
    window.removeEventListener('keydown', onKey);
    stage.dispose();
    panel.destroy();
    sim.dispose();
  };
}

/**
 * Host a room: lobby first, then the match. The host owns the sim and
 * broadcasts snapshots at 30 Hz.
 */
export async function startHostRoom(
  canvas: HTMLCanvasElement,
  room: string,
  status: StatusSink,
): Promise<Teardown> {
  await initPhysics();

  // Built when the match starts, from the sides the lobby settled on.
  let sim: VolleySim | null = null;
  let session: HostSession | null = null;
  let loop: FixedLoop | null = null;
  let panel: TuningPanel | null = null;
  const constants = cloneConstants(DEFAULTS);
  const stage = makeStage(canvas, constants);

  const hostRoom = new HostRoom(
    makeSignaling(room),
    room,
    {
      onLobbyChange: () => {
        view.render();
        const n = hostRoom.state.players.length;
        status(`Room ${room} — ${n} ${n === 1 ? 'player' : 'players'}`);
      },
    },
    { hostName: 'You', game: DEFAULT_GAME },
  );

  const view = new LobbyView({
    isHost: true,
    selfId: hostRoom.lobby.hostId,
    state: () => hostRoom.state,
    setName: (name) => hostRoom.setHostName(name),
    setSide: (side) => {
      hostRoom.lobby.applyIntent(hostRoom.lobby.hostId, { side });
      hostRoom.publish();
    },
    setTeamSize: (size) => hostRoom.setTeamSize(size),
    setGame: (id) => hostRoom.setGame(id),
    start: () => beginMatch(),
    inviteUrl: () => inviteUrl(room),
  });

  function endMatch(): void {
    loop?.stop();
    loop = null;
    panel?.destroy();
    panel = null;
    session = null;
    sim?.dispose();
    sim = null;
    stage.touch?.setVisible(false);
    hostRoom.endMatch();
    view.show();
    expose({ mode: 'host', hostRoom });
  }

  function beginMatch(): void {
    if (!hostRoom.canStart()) return;
    const sides = hostRoom.lobby.roster().sides as Side[];
    // Keep the tuning from the previous match so a tuning pass survives a rematch.
    sim = new VolleySim({ sides, net: true, constants });
    Object.assign(constants, sim.constants);
    session = hostRoom.beginMatch(sim);
    view.hide();

    panel = new TuningPanel(sim.constants, {
      actions: [
        { label: 'Rematch', run: () => sim?.resetMatch(0) },
        { label: 'Back to lobby', run: () => endMatch() },
      ],
    });

    const activeSim = sim;
    const activeSession = session;
    let prev = activeSim.snapshot();
    let next = prev;
    const hud = stage.touch
      ? { bottomInset: TOUCH_BAND_PX }
      : { hud: `HOST  room ${room}      ${stage.keysHint}      R rematch      Esc lobby      ~ tuning` };
    const draw = (alpha: number): void => {
      stage.touch?.setEnergy(next.blobs[0]?.energy ?? 1);
      stage.renderer.draw(prev, next, alpha, {
        net: true,
        match: next.match,
        charged: chargedFlags(next, activeSession.currentInputs()),
        ...hud,
      });
    };

    const stepOnce = (): void => {
      prev = next;
      activeSession.localInput = stage.localInput();
      activeSession.step();
      next = activeSim.snapshot();
    };

    const active = new FixedLoop({ step: stepOnce, render: draw });
    loop = active;
    stage.touch?.setVisible(true);
    active.start();
    stage.renderer.resize();
    status(`Room ${room} — playing as ${playerLabel(sides[0] ?? 0, 0)}`);
    expose({
      mode: 'host',
      sim: activeSim,
      host: activeSession,
      hostRoom,
      pause: () => active.stop(),
      step: stepOnce,
      renderOnce: () => {
        prev = activeSim.snapshot();
        next = prev;
        draw(0);
      },
    });
  }

  const onKey = (e: KeyboardEvent): void => {
    if (panel?.visible) return;
    if (e.code === 'KeyR' && sim) sim.resetMatch(0);
    if (e.code === 'Escape' && sim) endMatch();
  };
  window.addEventListener('keydown', onKey);

  await hostRoom.start();
  view.show();
  expose({ mode: 'host', hostRoom });
  status(`Room ${room} — waiting for players`);

  return () => {
    window.removeEventListener('keydown', onKey);
    loop?.stop();
    panel?.destroy();
    view.destroy();
    stage.dispose();
    hostRoom.close();
    sim?.dispose();
  };
}

/** Join a room: lobby, then render the host's snapshots. */
export async function startGuestRoom(
  canvas: HTMLCanvasElement,
  room: string,
  status: StatusSink,
): Promise<Teardown> {
  const constants = cloneConstants(DEFAULTS);
  const stage = makeStage(canvas, constants);
  let playing = false;
  let lastStatus = 0;
  let slot = -1;
  let tickParity = 0;

  /** Built from the first snapshot, once the blob's true state is known. */
  let predictor: BlobPredictor | null = null;

  const guestRoom = new GuestRoom(
    makeSignaling(room),
    {
      onLobbyChange: () => view.render(),
      onOpen: () => status(`Room ${room} — connected`),
      onClose: () => {
        playing = false;
        predictor = null;
        stage.touch?.setVisible(false);
        view.show();
        status(`Room ${room} — host disconnected`);
      },
      onStart: (startSlot, _sides, hostConstants) => {
        Object.assign(constants, hostConstants);
        slot = startSlot;
        predictor = null;
        playing = true;
        view.hide();
        stage.touch?.setVisible(true);
        stage.renderer.resize();

        const session = guestRoom.session;
        if (session) {
          session.onSnapshot = (state) => {
            // First snapshot: adopt the blob wholesale. After that, correct the
            // prediction only when it actually disagrees.
            const mine = state.blobs[slot];
            if (!mine) return;
            predictor ??= new BlobPredictor(mine, constants, true, state.tick);
            predictor.reconcile(state, slot);
          };
        }
        const shared = {
          guestRoom,
          predictor: () => predictor,
          pause: () => loop.stop(),
        };
        expose(
          session
            ? { mode: 'guest', guest: session, ...shared }
            : { mode: 'guest', ...shared },
        );
      },
      onEnd: () => {
        playing = false;
        predictor = null;
        stage.touch?.setVisible(false);
        view.show();
        status(`Room ${room} — back in the lobby`);
      },
    },
    () => performance.now(),
  );

  const view = new LobbyView({
    isHost: false,
    selfId: guestRoom.peerId,
    state: () => guestRoom.lobby,
    setName: (name) => guestRoom.sendIntent({ name }),
    setSide: (side) => guestRoom.sendIntent({ side }),
    setTeamSize: () => undefined,
    setGame: () => undefined,
    start: () => undefined,
    inviteUrl: () => inviteUrl(room),
  });

  let input: BlobInput = { left: false, right: false, jump: false, attack: false };
  const loop = new FixedLoop({
    step: () => {
      const session = guestRoom.session;
      if (!playing || !session) return;
      input = stage.localInput();
      predictor?.step(input);
      if (tickParity++ % INPUT_EVERY_TICKS === 0) session.sendInput(predictor?.tick ?? 0, input);
    },
    render: (_alpha, now) => {
      const session = guestRoom.session;
      if (!playing || !session) {
        if (playing) drawWaiting(stage.renderer, constants);
        return;
      }
      session.pingIfDue(now);

      const seen = session.view(now);
      if (!seen) {
        drawWaiting(stage.renderer, constants);
        return;
      }

      // Everything but your own blob comes from the host, ~80 ms in the past.
      // Your blob comes from the prediction, which is standing on now.
      const prev = withPredictedBlob(seen.prev, slot, predictor);
      const next = withPredictedBlob(seen.next, slot, predictor);
      stage.touch?.setEnergy(next.blobs[slot]?.energy ?? 1);
      // Only your own charge is known here (the host does not send inputs), so
      // only your own blob glows on a guest screen. The bar is in the snapshot.
      const charged = next.blobs.map((_, i) => i === slot && input.attack && (next.blobs[i]?.energy ?? 0) > 0);
      stage.renderer.draw(prev, next, seen.alpha, {
        net: true,
        match: next.match,
        charged,
        ...(stage.touch
          ? { bottomInset: TOUCH_BAND_PX }
          : { hud: `GUEST  room ${room}      ${stage.keysHint}` }),
      });

      if (now - lastStatus > 500) {
        lastStatus = now;
        const rtt = session.rttMs === null ? '—' : `${Math.round(session.rttMs)} ms`;
        const blobs = next.blobs;
        const mine = blobs[slot];
        const label = mine ? playerLabel(mine.side, indexWithinSide(blobs, slot)) : '…';
        status(`Room ${room} — playing as ${label} — rtt ${rtt}`);
      }
    },
  });

  await guestRoom.start();
  view.show();
  status(`Joining room ${room}…`);
  loop.start();
  expose({ mode: 'guest', guestRoom });

  return () => {
    loop.stop();
    view.destroy();
    stage.dispose();
    guestRoom.close();
  };
}

/** Which blobs are spending energy, for the renderer's glow. */
function chargedFlags(state: SimState, inputs: readonly BlobInput[]): boolean[] {
  return state.blobs.map((blob, i) => {
    const input = inputs[i];
    return input ? isCharged(blob, input) : false;
  });
}

/** One player against the computer. Same loop as a local match; blob 1 thinks. */
export async function startBot(
  canvas: HTMLCanvasElement,
  level: BotLevel,
  status: StatusSink,
): Promise<Teardown> {
  await initPhysics();
  const sim = new VolleySim({ sides: [0, 1], net: true });
  const bot = new Bot(1, level, sim.constants, Date.now() >>> 0);
  const stage = makeStage(canvas, sim.constants);
  const panel = new TuningPanel(sim.constants, {
    actions: [{ label: 'Rematch', run: () => sim.resetMatch(0) }],
  });

  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'KeyR' && !panel.visible) sim.resetMatch(0);
  };
  window.addEventListener('keydown', onKey);
  status(`You are Teal — ${level} bot on Pink`);

  let prev = sim.snapshot();
  let next = prev;
  let inputs: BlobInput[] = [];
  const hud = stage.touch
    ? { bottomInset: TOUCH_BAND_PX }
    : { hud: `${stage.keysHint}      R rematch      ~ tuning` };
  const draw = (alpha: number): void => {
    stage.touch?.setEnergy(next.blobs[0]?.energy ?? 1);
    stage.renderer.draw(prev, next, alpha, {
      net: true,
      match: next.match,
      charged: chargedFlags(next, inputs),
      ...hud,
    });
  };

  const stepOnce = (): void => {
    prev = next;
    inputs = [stage.localInput(), bot.think(next)];
    sim.step(inputs);
    next = sim.snapshot();
  };

  const loop = new FixedLoop({ step: stepOnce, render: draw });
  stage.touch?.setVisible(true);
  loop.start();
  expose({
    mode: 'bot',
    sim,
    pause: () => loop.stop(),
    step: stepOnce,
    renderOnce: () => {
      prev = sim.snapshot();
      next = prev;
      draw(0);
    },
  });

  return () => {
    loop.stop();
    window.removeEventListener('keydown', onKey);
    stage.dispose();
    panel.destroy();
    sim.dispose();
  };
}

/**
 * Swap in the locally predicted blob. Both interpolation endpoints get the same
 * predicted state, so your own blob is not dragged backwards by the buffer.
 */
function withPredictedBlob(
  state: SimState,
  slot: number,
  predictor: BlobPredictor | null,
): SimState {
  if (!predictor || slot < 0 || slot >= state.blobs.length) return state;
  const blobs = state.blobs.slice();
  blobs[slot] = predictor.blob;
  return { ...state, blobs };
}

/** Empty court, so a joining guest sees something before the first snapshot. */
function drawWaiting(renderer: CourtRenderer, constants: Constants): void {
  const empty: SimState = {
    tick: 0,
    ball: {
      x: constants.courtWidth / 2,
      y: constants.courtHeight * 0.8,
      vx: 0,
      vy: 0,
      spin: 0,
      angle: 0,
    },
    blobs: [],
    match: {
      phase: 'serve',
      phaseTicks: 0,
      score: [0, 0],
      server: 0,
      touchSide: null,
      touches: 0,
      lastPointWinner: null,
      lastFault: null,
      winner: null,
    },
  };
  renderer.draw(empty, empty, 0, { net: true, hud: 'waiting for the host…' });
}
